import * as AR from 'ApplicationEntry/ducks/agentInformation';
import * as AL from 'ApplicationEntry/ducks/agentList';
import * as BA from 'ApplicationEntry/ducks/billingAgent';
import * as B from 'ApplicationEntry/ducks/billingModes';
import * as E from 'fp-ts/lib/Either';
import * as BAActions from 'ApplicationEntry/ducks/billingAgent';
import * as PremiumActions from 'ApplicationEntry/ducks/premium';
import {push} from 'connected-react-router';
import {all, call, put, takeEvery, takeLatest} from 'redux-saga/effects';
import {AsyncResult, Loaded} from 'Core/models/Async';
import {
  getAgentNameByAgentNumber,
  getForm,
  getBillingModes,
  getCaseName,
  isAllstateAgent,
  getSalaryDeductionBillingInfo,
} from 'ApplicationEntry/api/awdApi';
import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import {createSnackbar} from 'Core/ducks/snackbar';
import {UserAssignedFormNumber} from 'ApplicationEntry/models/FormNumber';
import {AgentNumber, matchAgentNumber} from 'ApplicationEntry/models/AgentNumber';
import {ValidAgent} from 'ApplicationEntry/models/AgentInformation';

function* getCaseNameSaga() {
  yield takeEvery(BA.BillingAgentActionType.GET_CASE_NAME, function*(
    action: ReturnType<typeof BA.getCaseNameByCaseNumber>
  ) {
    const response: AsyncResult<{caseName: string}> = yield call(getCaseName, action.payload.caseNumber);

    if (E.isRight(response)) {
      yield put(BA.changeCaseNumber(new Loaded(action.payload.caseNumber)));
      yield put(BA.changeCaseName(response.right.caseName));
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(BA.changeCaseNumberError(action.payload.caseNumber));
    }
  });
}

function* getSalaryDeductionBillingInfoSaga() {
  yield takeLatest(BA.BillingAgentActionType.CHECK_CASE_NUMBER_BILLING, function*(
    action: ReturnType<typeof BA.checkCaseNumberBillingMode>
  ) {
    const response: AsyncResult<{
      saldBillingInfoDto: {billingMode: BillingMode; salaryDeductionBillingCode: string; caseName: string};
    }> = yield call(getSalaryDeductionBillingInfo, action.payload.caseNumber);
    if (E.isRight(response)) {
      yield put(BA.asyncCaseNumber(new Loaded(action.payload.caseNumber)));
      yield put(BA.getBillingModefromCaseNumber(response.right.saldBillingInfoDto?.salaryDeductionBillingCode));
      yield put(BA.changeCaseName(response.right?.saldBillingInfoDto?.caseName));
      if (response.right.saldBillingInfoDto?.salaryDeductionBillingCode === '4') {
        yield put(BA.getBillingModefromCaseNumber('Monthly'));
        yield put(BAActions.changeBillingMode('Monthly'));
        yield put(PremiumActions.changeBillMode('Monthly'));
      } else if (response.right.saldBillingInfoDto?.salaryDeductionBillingCode === '5') {
        yield put(BA.getBillingModefromCaseNumber('Weekly'));
        yield put(BAActions.changeBillingMode('Weekly'));
        yield put(PremiumActions.changeBillMode('Weekly'));
      }
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(BA.changeCaseNumberError(action.payload.caseNumber));
    }
  });
}

export function* watchFetchGetAgentNumberByAgentName() {
  yield takeLatest(AR.AgentInformationActionType.GET_AGENT_NAME, getAgentNumberByAgentNameSaga);
}

function* getAgentNumberByAgentNameSaga(action: ReturnType<typeof AR.getAgentName>) {
  const response: AsyncResult<{agent: {name: string}}> = yield call(
    getAgentNameByAgentNumber,
    action.payload.agentNumber.value
  );

  if (E.isRight(response)) {
    const agentName = response.right.agent.name;

    yield put(AR.updateAgentName(agentName));
  } else if (response.left.key === 'Unauthorized') {
    yield put(push(ApplicationEntryRouteConstants.AWDLogin));
  } else {
    yield put(AR.updateAgentNumberError(action.payload.agentNumber.value));
    yield put(AR.updateAgentName(''));
  }
}

function* isAllstateAffiliatedSaga(action: ReturnType<typeof AR.isAllstateAffiliated>) {
  const getAgentNumber = (agentNumber: AgentNumber) =>
    matchAgentNumber({
      Unassigned: () => '',
      Loading: () => '',
      Loaded: agentNumber => agentNumber,
      Error: () => '',
      UserAssignedAgentNumber: agentNumber => agentNumber,
      AgentNumberError: agentNumber => agentNumber,
    })(agentNumber);

  const agentNumber = getAgentNumber(action.payload.agentInformation.agentNumber)!;

  const response: AsyncResult<boolean> = yield call(isAllstateAgent, agentNumber);

  if (E.isRight(response)) {
    const result = response.right;

    const {editAgent, agentIndex, agentInformation} = action.payload;
    const {agentNumber, agentName, percent, signed} = agentInformation;

    const agent = ValidAgent.create(agentNumber, agentName, percent, signed, result);

    yield put(editAgent ? AL.updateAgent(agent, agentIndex) : AL.addAgent(agent));
    yield put(AR.emptyAgent());
  } else if (response.left.key === 'Unauthorized') {
    yield put(push(ApplicationEntryRouteConstants.AWDLogin));
  } else {
    yield put(createSnackbar("The agent's affiliation could not be determined."));
  }
}

export function* watchIsAllstateAffiliatedSaga() {
  yield takeLatest(AR.AgentInformationActionType.IS_ALLSTATE_AFFILIATED, isAllstateAffiliatedSaga);
}

export type Occurrence = {
  name: string;
  frequency: number;
};

export type BillingMode = {
  code: string;
  occurrences: Occurrence[];
};

function* getBillingModesSaga() {
  yield takeEvery(BA.BillingAgentActionType.GET_BILLING_MODE, function*(action: ReturnType<typeof BA.getBillingModes>) {
    const response: AsyncResult<{billingModes: BillingMode[]}> = yield call(getBillingModes);

    if (E.isRight(response)) {
      let billingModes = [] as BillingMode[];
      billingModes = response.right.billingModes;

      const nyBillingMode = ['Weekly', 'Monthly'];

      if (action.payload.applicationType === 'NY') {
        billingModes = billingModes.filter(ele => ele.occurrences.some(o => nyBillingMode.indexOf(o.name) !== -1));
      }

      if (action.payload.applicationType === 'GROUP') {
        billingModes = billingModes.map(mode => {
          return {code: mode.code, occurrences: mode.occurrences.filter(o => o.name !== 'Thirteenthly')};
        });
      }

      yield put(B.getBillingModeList(billingModes));
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(createSnackbar('Billing modes were not retrieved.'));
    }
  });
}

function* getAppNumbersSaga() {
  yield takeEvery(BA.BillingAgentActionType.GET_APP_NUMBER, function*(
    action: ReturnType<typeof BA.validateFormNumber>
  ) {
    const {payload} = action;
    const {formNumber} = payload;
    const response: AsyncResult<{
      form: {
        message: string[];
        documentLongName: string;
        documentName: string;
        documentShortName: string;
        fipscoDocumentName: string;
      };
    }> = yield call(getForm, formNumber);

    if (E.isRight(response)) {
      const {right} = response;
      const {documentShortName, fipscoDocumentName, documentLongName, documentName} = right.form;
      yield put(
        BA.changeFormNumber(
          new Loaded(
            UserAssignedFormNumber.create(
              formNumber,
              documentShortName,
              fipscoDocumentName,
              documentLongName,
              documentName
            )
          )
        )
      );
      yield put(
        BA.changeUserFormNumber(
          formNumber,
          documentShortName,
          fipscoDocumentName,
          documentShortName,
          fipscoDocumentName
        )
      );
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(BA.changeFormNumberError(formNumber));
    }
  });
}

export default function*() {
  yield all([
    getCaseNameSaga(),
    watchFetchGetAgentNumberByAgentName(),
    watchIsAllstateAffiliatedSaga(),
    getBillingModesSaga(),
    getAppNumbersSaga(),
    getSalaryDeductionBillingInfoSaga(),
  ]);
}
